import React from "react";
import {
  Box,
  Text,
  Button,
  Flex,
  HStack,
  VStack,
  Icon,
  Input,
  IconButton,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Center,
  Spinner,
} from "@chakra-ui/react";
import AnalystDataScope from "modules/analyst/containers/AnalystDataScope";
import { AnalystProto, DataScopeProto } from "build-ts/analyst";
import AnalystMemoryFactsContainer from "modules/analyst/containers/AnalystMemoryFacts";
import { AppIcons } from "constants/icons";

export interface AnalystDetailViewProps {
  // Identity
  analystId: string | undefined;
  isCreatingNew: boolean;

  // Analyst data
  analyst: AnalystProto | undefined;
  isLoadingAnalystDetails: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  isScopeLocked: boolean;
  analystSavedAt: number;

  // Name editing
  isEditingAnalystName: boolean;
  analystNameDraft: string;
  setAnalystNameDraft: (v: string) => void;
  setIsEditingAnalystName: (v: boolean) => void;
  onAnalystNameChange: () => void;

  // Tabs
  analystViewTabIndex: number;
  onTabChange: (idx: number) => void;
  showFooterBar: boolean;

  // Scope tab — passed through to AnalystDataScopeContainer
  setAnalyst: React.Dispatch<React.SetStateAction<AnalystProto | undefined>>;
  hasDiscardedChanges: boolean;
  setHasDiscardedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  originalAnalystData: AnalystProto | undefined;
  onAttributesNeedRefreshChange: (v: boolean) => void;
  onScopeLockedChange: (v: boolean) => void;
  refreshAttributeValuesRef: React.MutableRefObject<(() => Promise<void>) | null>;
  onTriggerAnalystSave: ((dataScopeOverride?: DataScopeProto) => Promise<void>) | undefined;

  // Actions
  onSave: () => void;
  onCancel: () => void;
  onOpenChat: () => void;
  onBack: () => void;
}

const AnalystDetailView: React.FC<AnalystDetailViewProps> = ({
  analystId,
  isCreatingNew,
  analyst,
  isLoadingAnalystDetails,
  isSaving,
  hasChanges,
  isScopeLocked,
  analystSavedAt,
  isEditingAnalystName,
  analystNameDraft,
  setAnalystNameDraft,
  setIsEditingAnalystName,
  onAnalystNameChange,
  analystViewTabIndex,
  onTabChange,
  showFooterBar,
  setAnalyst,
  hasDiscardedChanges,
  setHasDiscardedChanges,
  originalAnalystData,
  onAttributesNeedRefreshChange,
  onScopeLockedChange,
  refreshAttributeValuesRef,
  onTriggerAnalystSave,
  onSave,
  onCancel,
  onOpenChat,
  onBack,
}) => {
  if (isLoadingAnalystDetails) {
    return (
      <Center height="100%">
        <VStack spacing={4}>
          <Spinner size="xl" color="purple.600" thickness="3px" />
          <Text color="gray.600" fontSize="md" mb={0}>
            Loading. Please hold on...
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Flex direction="column" height="100%" bg="white" flex="1" minH={0}>
      <Tabs
        colorScheme="purple"
        variant="line"
        index={analystViewTabIndex}
        onChange={onTabChange}
        display="flex"
        flexDirection="column"
        flex="1"
        minH={0}
        w="100%"
      >
        {/* Header: back, avatar + editable name, tab list, Start Chat */}
        <HStack
          px={3}
          mt={1}
          pb={0.5}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="flex-end"
          gap={0}
          w="100%"
          spacing={0}
          flexShrink={0}
        >
          <HStack spacing={3} minW={0} pr={1} flexShrink={0} maxW="min(24rem, 40vw)" alignSelf="center" align="center">
            <Button
              leftIcon={<Icon as={AppIcons.leftArrow} boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={onBack}
              color="gray.700"
              _hover={{ bg: "gray.100" }}
              flexShrink={0}
              iconSpacing={0}
              aria-label="Back to analysts"
            />
            <HStack spacing={3} align="center" minW={0} flex="1" maxW="100%">
              <Icon as={AppIcons.analyst} boxSize={7} color="purple.600" />
              {isEditingAnalystName ? (
                <HStack spacing={2} align="center" minW={0} maxW="100%" flex={1}>
                  <Input
                    variant="flushed"
                    size="lg"
                    fontWeight="600"
                    color="gray.800"
                    autoFocus
                    placeholder={isCreatingNew ? "Name your analyst" : "Analyst name"}
                    value={analystNameDraft}
                    onChange={e => setAnalystNameDraft(e.target.value)}
                    onBlur={onAnalystNameChange}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        onAnalystNameChange();
                      } else if (e.key === "Escape") {
                        setAnalystNameDraft(analyst?.header?.name || "");
                        setIsEditingAnalystName(false);
                      }
                    }}
                    minW="0"
                    flex={1}
                    borderColor="purple.400"
                    _focusVisible={{ borderColor: "purple.500", boxShadow: "none" }}
                  />
                  <IconButton
                    aria-label="Save analyst name"
                    icon={<Icon as={AppIcons.check} boxSize={5} color="green.500" />}
                    size="xs"
                    colorScheme="green"
                    variant="ghost"
                    flexShrink={0}
                    onMouseDown={e => e.preventDefault()}
                    onClick={onAnalystNameChange}
                  />
                </HStack>
              ) : (
                <HStack
                  role="group"
                  spacing={2}
                  align="center"
                  minW={0}
                  maxW="100%"
                  cursor="pointer"
                  onClick={() => {
                    setAnalystNameDraft(analyst?.header?.name || "");
                    setIsEditingAnalystName(true);
                  }}
                  _hover={{ opacity: 0.92 }}
                  flex="1"
                >
                  <Text
                    fontSize="md"
                    fontWeight="700"
                    color="gray.800"
                    mb={0}
                    textAlign="left"
                    display="block"
                    noOfLines={1}
                    lineHeight="short"
                    py={0}
                    borderBottomWidth="2px"
                    borderColor="transparent"
                    _groupHover={{ borderColor: "purple.200" }}
                  >
                    {analyst?.header?.name || (isCreatingNew ? "New analyst" : "Untitled analyst")}
                  </Text>
                  <Icon
                    as={AppIcons.edit}
                    boxSize={4}
                    color="gray.600"
                    opacity={0.6}
                    _groupHover={{ opacity: 1 }}
                    flexShrink={0}
                  />
                </HStack>
              )}
            </HStack>
          </HStack>

          <Flex flex="1" minW={0} pl={1} pr={0} align="flex-end" justify="flex-end" overflow="visible">
            <TabList w="auto" maxW="100%" display="flex" flexShrink={1} minW={0} gap={0} px={0} borderBottom="none">
              <Tab
                isDisabled={isLoadingAnalystDetails}
                fontWeight="600"
                _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
              >
                <HStack as="span" spacing={2} whiteSpace="nowrap" justify="flex-start" align="center" flex="1" minW={0}>
                  <Icon as={AppIcons.analystScope} boxSize={6} flexShrink={0} />
                  <Text as="span" display="inline" fontSize="md" mb={0} textAlign="left">
                    Scope
                  </Text>
                </HStack>
              </Tab>
              {!isCreatingNew && (
                <Tab
                  isDisabled={isLoadingAnalystDetails}
                  fontWeight="600"
                  _selected={{ fontWeight: "700", borderBottomWidth: "3px", borderColor: "purple.700" }}
                >
                  <HStack
                    as="span"
                    spacing={2}
                    whiteSpace="nowrap"
                    justify="flex-start"
                    align="center"
                    flex="1"
                    minW={0}
                  >
                    <Icon as={AppIcons.analystMemory} boxSize={6} flexShrink={0} />
                    <Text as="span" display="inline" fontSize="md" mb={0} textAlign="left">
                      Memory
                    </Text>
                  </HStack>
                </Tab>
              )}
            </TabList>
          </Flex>

          <Box flexShrink={0} pl={2} minW="min-content" alignSelf="center">
            {analystId && !isCreatingNew && (
              <Button
                leftIcon={<Icon as={AppIcons.analystChat} boxSize={4} />}
                onClick={onOpenChat}
                size="md"
                colorScheme="purple"
                bg="purple.700"
                _hover={{ bg: "purple.600" }}
                color="white"
                fontWeight="600"
                fontSize="md"
                whiteSpace="nowrap"
              >
                Start Chat
              </Button>
            )}
          </Box>
        </HStack>

        {/* Tab panels */}
        <TabPanels flex="1" minH={0} display="flex" flexDirection="column" overflow="auto" w="100%">
          {/* Scope */}
          <TabPanel p={0} pt={3} flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden">
            <VStack align="stretch" spacing={3} flex="1" minH={0} overflow="auto">
              {analystViewTabIndex === 0 && (
                <AnalystDataScope
                  analyst={analyst}
                  setAnalyst={setAnalyst}
                  isCreatingNew={isCreatingNew}
                  hasDiscardedChanges={hasDiscardedChanges}
                  setHasDiscardedChanges={setHasDiscardedChanges}
                  originalAnalyst={originalAnalystData}
                  onAttributesNeedRefreshChange={onAttributesNeedRefreshChange}
                  onScopeLockedChange={onScopeLockedChange}
                  refreshAttributeValuesRef={refreshAttributeValuesRef}
                  analystSavedAt={analystSavedAt}
                  onTriggerAnalystSave={onTriggerAnalystSave}
                />
              )}
            </VStack>
          </TabPanel>

          {/* Memory — only needs analystId, no full proto required */}
          {!isCreatingNew && (
            <TabPanel p={0} flex="1" minH={0} overflowY="auto">
              {analystId && <AnalystMemoryFactsContainer analystId={analystId} />}
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Footer: only when creating or has unsaved changes */}
      {showFooterBar && (
        <Flex
          flexShrink={0}
          borderTop="1px"
          borderColor="gray.200"
          py={2}
          px={6}
          bg="white"
          align="center"
          justify="flex-end"
          gap={3}
        >
          {hasChanges && (
            <Text fontSize="md" color="gray.700" fontWeight="600" mb={0}>
              You have unsaved changes.
            </Text>
          )}
          {!hasChanges && <Box flex="1" />}

          <HStack spacing={3}>
            {(isCreatingNew || hasChanges) && (
              <Button
                colorScheme="green"
                size="md"
                onClick={onSave}
                isLoading={isSaving}
                isDisabled={isScopeLocked || isSaving}
                loadingText={isCreatingNew ? "Saving..." : "Updating..."}
                leftIcon={<Icon as={AppIcons.check} boxSize={5} />}
                fontWeight="600"
                transition="all 0.2s"
                fontSize="md"
              >
                {isCreatingNew ? "Save" : "Update"}
              </Button>
            )}
            {(isCreatingNew || hasChanges) && (
              <Button
                colorScheme="red"
                variant="outline"
                size="md"
                onClick={onCancel}
                isDisabled={isSaving || isScopeLocked}
                leftIcon={<Icon as={AppIcons.undo} boxSize={5} />}
                fontWeight="600"
                fontSize="md"
              >
                {isCreatingNew ? "Cancel" : "Discard"}
              </Button>
            )}
          </HStack>
        </Flex>
      )}
    </Flex>
  );
};

export default AnalystDetailView;
